import React, { useState } from "react";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LogMessage from "../LogMessage";
import VideoCallSummaryMessage, { resolveVideoCallSummary } from "../VideoCallSummaryMessage";
import VoiceCallLogMessage, { resolveVoiceCallLog } from "../VoiceCallLogMessage";
import PartnerApiAuditPanel from "../PartnerApiAuditPanel";
import AiAgentAuditPanel from "../AiAgentAuditPanel";
import {
  getMessageDate,
  setActivityExpandPreference,
} from "../timelineUtils";
import ActivityLogDetail from "./ActivityLogDetail";
import {
  activityHasDetail,
  activityTimeTitle,
  collapseIdenticalLogs,
  formatActivityClock,
  formatActivityClockRange,
  getActivityActor,
  getActivityPresentation,
  getGroupActorLabel,
  getTaskLogRef,
} from "../activityLogDisplay";
import { useActivityTaskOpener } from "../ActivityTaskModal";

const formatTime = (message) => formatActivityClock(getMessageDate(message));

const ActivityLogGroup = ({ messages = [], defaultExpanded = false, loggedUser, leadId = null }) => {
  const theme = useTheme();
  const hasAnyDetail = messages.some((message) => activityHasDetail(message));
  const [expanded, setExpanded] = useState(defaultExpanded && hasAnyDetail);
  const [taskHints, setTaskHints] = useState({});
  const openActivityTask = useActivityTaskOpener();
  const actor = getGroupActorLabel(messages, loggedUser);
  const time = formatActivityClockRange(messages);
  const rows = collapseIdenticalLogs(messages, loggedUser);

  if (!messages.length || messages.length === 1) return null;

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    setActivityExpandPreference(next);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        py: 0.25,
      }}
    >
      <Box sx={{ maxWidth: "min(440px, 92%)", width: "100%", textAlign: "center" }}>
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderLeft: `2px solid ${theme.palette.divider}`,
            textAlign: "left",
          }}
        >
          <Typography
            component="div"
            variant="caption"
            sx={{ color: theme.palette.text.disabled, lineHeight: 1.3, mb: 0.35 }}
          >
            {actor?.label || ""}
            {actor?.deleted ? (
              <Box
                component="span"
                sx={{
                  ml: 0.6,
                  px: 0.6,
                  borderRadius: 10,
                  border: "1px solid",
                  borderColor: "divider",
                  fontSize: "0.65rem",
                }}
              >
                excluído
              </Box>
            ) : null}
            {time ? ` · ${time}` : ""}
          </Typography>
          {rows.map((row) => {
            const presentation = getActivityPresentation(row.message);
            const taskRef = getTaskLogRef(row.message);
            const openTask = async (event) => {
              event?.stopPropagation?.();
              event?.preventDefault?.();
              if (!taskRef) return;
              setTaskHints((prev) => ({ ...prev, [row.message.id]: "" }));
              if (!openActivityTask) {
                setTaskHints((prev) => ({ ...prev, [row.message.id]: "Tarefa não encontrada" }));
                return;
              }
              try {
                await openActivityTask({ cardId: taskRef.cardId, title: taskRef.title, leadId });
              } catch {
                setTaskHints((prev) => ({ ...prev, [row.message.id]: "Tarefa não encontrada" }));
              }
            };
            return (
              <Box key={row.message.id} sx={{ py: 0.35 }} title={activityTimeTitle(getMessageDate(row.message))}>
                <Typography
                  component="div"
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.35,
                  }}
                >
                  {taskRef ? (
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      onClick={openTask}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") openTask(event);
                      }}
                      sx={{
                        color: "inherit",
                        cursor: "pointer",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {presentation.headline}
                    </Box>
                  ) : (
                    presentation.headline
                  )}
                  {row.repeat > 1 ? ` · ${row.repeat} vezes` : ""}
                </Typography>
                {taskHints[row.message.id] ? (
                  <Typography component="div" variant="caption" sx={{ color: theme.palette.text.disabled, display: "block" }}>
                    {taskHints[row.message.id]}
                  </Typography>
                ) : null}
                {presentation.origin ? (
                  <Typography component="div" variant="caption" sx={{ color: theme.palette.text.disabled, display: "block" }}>
                    {presentation.origin}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
          {hasAnyDetail ? (
            <Box
              component="button"
              type="button"
              onClick={toggle}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                color: theme.palette.text.disabled,
                fontSize: "0.68rem",
                p: 0,
                mt: 0.25,
              }}
            >
              {expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
              {expanded ? "Ocultar detalhe" : "Ver detalhe"}
            </Box>
          ) : null}
        </Box>

        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 0.25,
              pl: 1.5,
              borderLeft: `2px solid ${theme.palette.divider}`,
              ml: 1,
            }}
          >
            {rows.map((row) => {
              const msg = row.message;
              const rowActor = getActivityActor(msg, loggedUser);
              const extra = getActivityPresentation(msg).extra;
              return (
              <Box
                key={msg.id}
                sx={{
                  py: 0.5,
                  fontSize: "0.75rem",
                  color: theme.palette.text.secondary,
                  "& .MuiTypography-root": { fontSize: "0.75rem !important" },
                }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  title={activityTimeTitle(getMessageDate(msg))}
                  sx={{ fontSize: "0.68rem", color: theme.palette.text.disabled, mb: 0.15 }}
                >
                  {formatTime(msg)}
                  {row.repeat > 1 ? ` · ${row.repeat} vezes` : ""}
                  {rowActor.label ? ` · ${rowActor.label}` : ""}
                  {rowActor.deleted ? " · excluído" : ""}
                </Typography>
                {extra && !getTaskLogRef(msg) ? (
                  <Typography variant="caption" component="div" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", mb: 0.5 }}>
                    {extra}
                  </Typography>
                ) : null}
                {resolveVoiceCallLog(msg) ? (
                  <VoiceCallLogMessage message={msg} />
                ) : resolveVideoCallSummary(msg) ? (
                  <Box sx={{ mt: 0.5, textAlign: "left" }}>
                    <VideoCallSummaryMessage message={msg} />
                  </Box>
                ) : getTaskLogRef(msg) ? null : activityHasDetail(msg) ? (
                  <ActivityLogDetail message={msg} />
                ) : (
                  <LogMessage message={msg} loggedUser={loggedUser} />
                )}
                <PartnerApiAuditPanel message={msg} compact />
                <AiAgentAuditPanel message={msg} compact />
              </Box>
              );
            })}
          </Box>
        </Collapse>

      </Box>
    </Box>
  );
};

export default ActivityLogGroup;
